// Wrapper for Sleeper API calls

/**
 * DTO for season stats from Sleeper API
 */
export interface SeasonStats {
  player_id: string;
  team: string;
  fpts_ppr?: number;
  fpts_half_ppr?: number;
  fpts_std?: number;
  games_played?: number;
  gp?: number;
  rec_tar?: number;
  rush_att?: number;
  redzone_targets?: number;
  air_yards?: number;
}

/**
 * Fetch season stats for all players
 * @param season - the year (e.g., 2024)
 * @param seasonType - 'regular' | 'post' | 'pre' | 'off'
 */
export async function fetchSeasonStats(
  season: number,
  seasonType: 'regular' | 'post' | 'pre' | 'off' = 'regular'
): Promise<SeasonStats[]> {
  // Try multiple possible URLs for season stats
  const urls = [
    `https://api.sleeper.app/v1/stats/nfl/${seasonType}/${season}`,
    `https://api.sleeper.app/v1/stats/nfl/${season}/${seasonType}`,
    `https://api.sleeper.app/stats/nfl/${seasonType}/${season}`,
    `https://api.sleeper.app/stats/nfl/${season}/${seasonType}`
  ];
  
  for (const url of urls) {
    console.log('Trying fetchSeasonStats URL:', url);
    try {
      const res = await fetch(url);
      if (res.ok) {
        const data = await res.json();
        console.log(`Successfully fetched ${Object.keys(data).length} season stats records from ${url}`);
        return Array.isArray(data) ? data : Object.values(data);
      }
    } catch (error) {
      console.log(`Failed to fetch from ${url}:`, error);
    }
  }
  
  // If 2024 fails, try fallback to 2023
  if (season === 2024) {
    console.log('2024 stats not available, falling back to 2023');
    return fetchSeasonStats(2023, seasonType);
  }
  
  console.error('Error fetching season stats: All URLs failed');
  return [];
}

/**
 * Fetch season projections (PPR) for all players
 * @param season - the year (e.g., 2024)
 * @param seasonType - 'regular' | 'post' | 'pre' | 'off'
 */
export async function fetchPlayerProjections(
  season: number,
  seasonType: 'regular' | 'post' | 'pre' | 'off' = 'regular'
): Promise<Record<string, number>> {
  // Fix URL to use correct api.sleeper.app domain
  const url = `https://api.sleeper.app/v1/projections/nfl/${season}/${seasonType}`;
  console.log('fetchPlayerProjections URL:', url);
  try {
    const res = await fetch(url);
    if (!res.ok) throw new Error(`Failed to fetch projections: ${res.status}`);
    const data = await res.json();
    console.log(`Fetched ${Object.keys(data).length} projection records`);

    // Log the raw structure of 'data'
    console.log('fetchPlayerProjections: Raw data (first 500 chars):', JSON.stringify(data).substring(0, 500));
    
    // Handle both array and object responses
    const dataArray = Array.isArray(data) ? data : Object.values(data);

    // Log the first few items of dataArray to inspect their structure
    if (dataArray.length > 0) {
      console.log('fetchPlayerProjections: Inspecting first 3 items of dataArray:');
      for (let i = 0; i < Math.min(3, dataArray.length); i++) {
        console.log(`Item ${i + 1}:`, JSON.stringify(dataArray[i]));
      }
    } else {
      console.log('fetchPlayerProjections: dataArray is empty.');
    }
    
    return dataArray.reduce<Record<string, number>>((map, s: any) => {
      if (s && s.player_id) {
        map[s.player_id] = s.fpts_ppr ?? s.fpts_half_ppr ?? s.fpts_std ?? 0;
      }
      return map;
    }, {});
  } catch (error) {
    console.error('Error fetching projections:', error);
    return {};
  }
}

/**
 * Fetch all NFL players from Sleeper API
 */
export async function fetchAllPlayers(): Promise<Record<string, any>> {
  const url = 'https://api.sleeper.app/v1/players/nfl';
  console.log('fetchAllPlayers URL:', url);
  try {
    const res = await fetch(url);
    if (!res.ok) throw new Error(`Failed to fetch players: ${res.status}`);
    const data = await res.json();
    console.log(`Fetched ${Object.keys(data).length} players`);
    return data;
  } catch (error) {
    console.error('Error fetching players:', error);
    return {};
  }
}
